import { useMsal } from '@azure/msal-react';
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { LabelIdValue, LabeledValue } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { StructureSet } from '~/domain/patient/structureset';
import { RoiEdits, StructureSetEdits } from '~/hooks/export/useExportEditing';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { formatDateString } from '~/utils/date';
import StructuresTable from './StructuresTable';

export default function StructureSetExportDetails({
    structureSet,
    selectedStructures,
    onSelectStructure,
    ssEdits,
    roiEdits,
    setSsLabel,
    setSsApproval,
    setSsReviewer,
    setRoiName,
    setRoiType,
}: {
    structureSet: StructureSet;
    selectedStructures: DicomRoi[];
    onSelectStructure: (structure: DicomRoi) => void;
    ssEdits: Record<string, StructureSetEdits>;
    roiEdits: RoiEdits;
    setSsLabel: (uid: string, label: string) => void;
    setSsApproval: (uid: string, status: DICOMApprovalStatus) => void;
    setSsReviewer: (uid: string, name: string) => void;
    setRoiName: (ssUid: string, roiNumber: number, name: string) => void;
    setRoiType: (
        ssUid: string,
        roiNumber: number,
        type: RoiInterpretedType
    ) => void;
}) {
    const [pendingApproval, setPendingApproval] =
        useState<DICOMApprovalStatus | null>(null);
    const intl = useIntl();
    const sessionData = useRecoilValue(sessionDataAtom);
    const { instance } = useMsal();
    const currentUserName =
        sessionData?.username ?? instance?.getActiveAccount()?.name;

    const uid = structureSet.dicomUid;
    const currentEdits = ssEdits[uid];
    const isAiGenerated = !!structureSet.generationModel;

    const effectiveApprovalStatus =
        currentEdits?.approvalStatus ?? structureSet.approvalStatus;
    const effectiveLabel =
        currentEdits?.label ?? structureSet.structureSetLabel;
    const effectiveIsApproved =
        effectiveApprovalStatus === DICOMApprovalStatus.APPROVED;

    // ROI name/type editing is only for AI-generated sets not yet SS-approved.
    // Pass as effectiveIsApproved to StructuresTable so StructureRow
    // doesn't need to know about isAiGenerated.
    const roiEditingDisabled = !isAiGenerated || effectiveIsApproved;

    const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSsLabel(uid, event.target.value);
    };

    const prefillReviewer = (newStatus: DICOMApprovalStatus) => {
        if (
            newStatus === DICOMApprovalStatus.APPROVED &&
            !currentEdits?.reviewerName &&
            !structureSet.reviewerName &&
            currentUserName
        ) {
            setSsReviewer(uid, currentUserName);
        }
    };

    const handleApprovalChange = (event: SelectChangeEvent<string>) => {
        const newStatus = event.target.value as DICOMApprovalStatus;
        if (!isAiGenerated) {
            setPendingApproval(newStatus);
        } else {
            setSsApproval(uid, newStatus);
            prefillReviewer(newStatus);
        }
    };

    const handleApprovalConfirm = () => {
        if (pendingApproval !== null) {
            setSsApproval(uid, pendingApproval);
            prefillReviewer(pendingApproval);
        }
        setPendingApproval(null);
    };

    const handleApprovalCancel = () => {
        setPendingApproval(null);
    };

    const handleReviewerChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSsReviewer(uid, event.target.value);
    };

    const ssRoiEdits = roiEdits[uid] ?? {};

    return (
        <>
            <Box display="grid" gap={2}>
                <Box display="grid" gap={1}>
                    <Box py={1} display="grid" gap={1}>
                        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr">
                            <Box>
                                <Typography
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.secondary"
                                    lineHeight={1.2}
                                >
                                    Name
                                </Typography>
                                {isAiGenerated ? (
                                    <TextField
                                        variant="standard"
                                        size="small"
                                        value={effectiveLabel}
                                        disabled={effectiveIsApproved}
                                        onChange={handleLabelChange}
                                        inputProps={{
                                            'data-testid':
                                                'export-structure-set-name',
                                        }}
                                        sx={{ mt: 0.25 }}
                                    />
                                ) : (
                                    <Typography
                                        fontSize={14}
                                        fontWeight={500}
                                        data-testid="export-structure-set-name"
                                    >
                                        <SensitiveInfoTypography>
                                            {effectiveLabel}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                )}
                            </Box>
                            <LabeledValue
                                label="Structures"
                                value={structureSet.roiArray.length}
                            />
                            <LabelIdValue
                                label="SOP Instance UID"
                                id={structureSet.dicomUid ?? ''}
                            />
                        </Box>
                        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr">
                            <Box>
                                <Typography
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.secondary"
                                    lineHeight={1.2}
                                >
                                    Approval status
                                </Typography>
                                <Select
                                    variant="standard"
                                    size="small"
                                    value={effectiveApprovalStatus}
                                    onChange={handleApprovalChange}
                                    data-testid="export-structure-set-approval-status"
                                    inputProps={{
                                        'aria-label': 'Approval status',
                                    }}
                                    sx={{ fontSize: 14, mt: 0.25 }}
                                >
                                    <MenuItem
                                        value={DICOMApprovalStatus.UNAPPROVED}
                                    >
                                        Unapproved
                                    </MenuItem>
                                    <MenuItem
                                        value={DICOMApprovalStatus.APPROVED}
                                    >
                                        Approved
                                    </MenuItem>
                                    <MenuItem
                                        value={DICOMApprovalStatus.REJECTED}
                                    >
                                        Rejected
                                    </MenuItem>
                                </Select>
                            </Box>
                            {effectiveIsApproved && (
                                <Box>
                                    <Typography
                                        fontSize={13}
                                        fontWeight={500}
                                        color="text.secondary"
                                        lineHeight={1.2}
                                    >
                                        Reviewer name
                                    </Typography>
                                    <TextField
                                        variant="standard"
                                        size="small"
                                        value={
                                            currentEdits?.reviewerName ??
                                            structureSet.reviewerName
                                        }
                                        onChange={handleReviewerChange}
                                        sx={{ mt: 0.25 }}
                                    />
                                </Box>
                            )}
                            {structureSet.approvalStatus ===
                                DICOMApprovalStatus.APPROVED &&
                                effectiveIsApproved && (
                                    <LabeledValue
                                        label="Review Date"
                                        value={formatDateString(
                                            structureSet.dicomStructureSet
                                                .reviewDate
                                        )}
                                        sensitive
                                    />
                                )}
                        </Box>
                    </Box>
                    <StructuresTable
                        structures={structureSet.roiArray ?? []}
                        selectedStructures={selectedStructures}
                        onSelect={onSelectStructure}
                        ssUid={uid}
                        effectiveIsApproved={roiEditingDisabled}
                        ssApprovalEdit={currentEdits?.approvalStatus}
                        roiEdits={ssRoiEdits}
                        setRoiName={(roiNumber, name) =>
                            setRoiName(uid, roiNumber, name)
                        }
                        setRoiType={(roiNumber, type) =>
                            setRoiType(uid, roiNumber, type)
                        }
                    />
                </Box>
            </Box>
            <ConfirmationDialog
                open={pendingApproval !== null}
                title={intl.formatMessage({
                    id: 'export.importedStructureSet.confirm.title',
                })}
                message={intl.formatMessage({
                    id: 'export.importedStructureSet.confirm.message',
                })}
                confirmText={intl.formatMessage({
                    id: 'export.importedStructureSet.confirm.proceed',
                })}
                mode="warning"
                onConfirm={handleApprovalConfirm}
                onCancel={handleApprovalCancel}
            />
        </>
    );
}
